import React, { useEffect, useState } from 'react';
import CustomCheckBox from 'components/CustomCheckBox';
import { extractJSONFromText, jsonToMarkdown } from 'utils/function';
import useAppState from 'context/useAppState';
import { getLLMAgentRAGCreatePayload, getLLMAgentRAGUpdatePayload } from 'services/llm/payloads';
import { useParams } from 'react-router-dom';
import { updateTask } from 'services/event';
import { cloneDeep, isArray } from 'lodash';
import allImgPaths from 'assets/images';
import Toast from 'components/common/Toast';

function ListItems({ messageData }: any) {
  const { content, _id: messageId } = messageData;
  const jsonDetails = extractJSONFromText(content);
  let { heading = null } = jsonDetails;
  const [items, setItems] = useState(jsonDetails?.items || [])

  const { setState: setChatMessagesState, updateMessageContent, getUserChatSessionMessages } = useAppState('chatMessages')
  const { currentSession, updateRAGEntryOfFete } = useAppState('chatBox')

  const { id, draftEventId } = useParams();
  const { boardData, cards: cardList, columns, updateState, selectedCardDetail } = useAppState("cardWrapper");
  const [currentTask, setCurrentTask] = useState<any>(null)
  const { draftEvent, draftEventTasks } = useAppState('newFeteChat')

  const [saveBtnDisable, setSaveBtnDisable] = useState(true)
  const [isEditMode, setIsEditMode] = useState(jsonDetails?.isEdit || false)
  const { setState: setEventDataState, sendButtonRef, currentTaskObj } = useAppState("eventData");
  const [selectedItems, setSelectedItems] = useState<any>([]);
  const [savedItems, setSavedItems] = useState<any>([]);

  const getEventId = () => {
    if (id) {
      return id;
    }
    if (draftEventId) {
      return draftEventId;
    }
    if (draftEvent && draftEvent?._id) {
      return draftEvent?._id;
    }
  }

  useEffect(() => {
    getCurrentTaskDetail()
  }, [draftEventTasks])

  useEffect(() => {
    fetchInitData()
    getCurrentTaskDetail()
  }, [])

  const fetchInitData = async () => {
    const chatSessionMessages = await getUserChatSessionMessages(currentSession?._id)
    setChatMessagesState({ messageHistory: [...chatSessionMessages] })
  }

  const getCurrentTaskDetail = () => {
    let currentTaskDetails: any;
    if (selectedCardDetail && id) {
      const updatedSelectedCardDetail = { ...selectedCardDetail }
      const latestTasks = updatedSelectedCardDetail?.tasks || [];
      const currentTask = latestTasks.find((t: any) => JSON.stringify(t._id) == JSON.stringify(jsonDetails.taskId));
      setCurrentTask(currentTask)
    } else if (draftEventTasks && draftEventTasks?.length > 0) {
      currentTaskDetails = draftEventTasks?.find((t: any) => t._id == jsonDetails.taskId);
      setCurrentTask(currentTaskDetails)
    }
    return currentTaskDetails;
  }

  const handleChange = async (index: number, isChecked: boolean) => {
    const updatedListItems = items.map((item: any, idx: any) => {
      if (idx === index) {
        return { ...item, selected: isChecked };
      }
      return item;
    });

    setItems(updatedListItems)

    // New Additions
    const clickedItem = items[index];
    let newSelectedItems;

    if (selectedItems.includes(clickedItem.title)) {
      newSelectedItems = selectedItems.filter((title: any) => title !== clickedItem.title);
    } else {
      newSelectedItems = [...selectedItems, clickedItem.title];
    }

    setSelectedItems(newSelectedItems);
    const isSaveDisabled =
      newSelectedItems.length === 0 ||
      newSelectedItems.every((title: any) => savedItems.includes(title));

    setSaveBtnDisable(isSaveDisabled)
    setEventDataState({ unsavedChanges: !isSaveDisabled })
  };

  const save = async (fromFavorite = false) => {
    setSaveBtnDisable(true)

    const currentTeam = JSON.parse(localStorage.getItem('selectedTeam') as string)

    const isDraft = draftEventId ? true : false

    const messagePayload = {
      content: JSON.stringify({ "heading": jsonDetails?.heading, 'items': items }),
      teamId: currentTeam?.currentTeamId,
      eventId: getEventId(),
      is_draft: isDraft,
      taskId: currentTaskObj?.task_id || currentTask?._id || jsonDetails.taskId,
      cardId: selectedCardDetail?._id,
      blockId: selectedCardDetail?.columnId || selectedCardDetail?.block_id
    }

    try {
      await updateMessageContent(messageId, messagePayload)
    } catch (error) {
      if (!fromFavorite) handleApiCases(false)
      return;
    }

    const selectedCardItems = cloneDeep(items?.filter((ci: any) => ci?.selected))
    selectedCardItems.map((fc: any) => {
      delete fc['selected']
    })

    setSavedItems([...savedItems, ...selectedItems]);
    setSelectedItems([]);
    setSaveBtnDisable(true);
    setEventDataState({ unsavedChanges: false })

    await handleRAGCall({ heading: currentTask?.title ? currentTask?.title : jsonDetails?.heading, items: selectedCardItems })

    if (!fromFavorite) handleApiCases(true)
  }

  const handleRAGCall = async (content: any) => {
    const userData = JSON.parse(localStorage.getItem('userData') as string)
    const userEmail = localStorage.getItem('userEmail')
    const currentTeam = JSON.parse(localStorage.getItem('selectedTeam') as string)

    let ragPayload: any = {
      session_id: currentSession?._id,
      text_content: jsonToMarkdown(content),
      text_prefix: ``
    }

    const isDraft = draftEventId ? true : false
    const ragMetaData = {
      userId: userData?.id,
      userEmail: userEmail,
      teamId: currentTeam?.currentTeamId,
      eventId: getEventId(),
      is_draft: isDraft,
      taskId: currentTaskObj?.task_id || currentTask?._id || jsonDetails.taskId,
      updatedAt: new Date().toISOString(),
      cardId: selectedCardDetail?._id
    }

    let currentTaskDetails = currentTask;
    if (!currentTaskDetails) {
      currentTaskDetails = getCurrentTaskDetail();
    }

    ragPayload = { ...ragPayload, document_path: messageId, additional_metadata: ragMetaData }

    try {
      await updateRAGEntryOfFete(getLLMAgentRAGUpdatePayload(ragPayload))
    } catch (error) {
      return;
    }
  }

  const handleApiCases = (isSuccess: boolean) => {
    if (isSuccess) {
      Toast.success('', 'Saved successfully')
    } else {
      Toast.error('', 'Failed to save')
    }
  }

  const setFavorite = async (index: number, value: boolean) => {
    const updatedListItems = items.map((item: any, idx: any) => {
      if (idx === index) {
        return { ...item, is_favorite: value };
      }
      return item;
    });
    setItems(updatedListItems)

    const isSelected = updatedListItems[index];
    if (isSelected?.selected) {
      await save(true)
    }
  }

  const showMore = () => {
    setEventDataState({ bestActionQuery: 'Brainstorm event themes', bestActionPrompt: currentTaskObj.promptKey || '', currentTaskObj })
    sendButtonRef.current = true;
  }

  return (
    <div className="rounded-lg border border-[#EAECF0] pt-3 pb-4 px-4 bg-[#F9FAFB] mb-6">
      {heading && (
        <p className='text-base text-[#101828] pb-1.5'>{heading}</p>
      )}
      <div className="border border-[#E6E6E6] rounded-lg px-2 py-2 bg-[#fff] shadow-customShadow10 gap-x-4 signup-slider-wrapper review-space-slider flex w-[calc(100vw_-_614px)] overflow-x-auto">
        {items && isArray(items) && items.length > 0 && items.map((item: any, index: number) => {
          return (
            <div
              key={index}
              className={`${item?.selected ? "border-[#A59DFB] border-2" : "border-[#D8D8D8] border-2"} px-4 py-3.5 bg-white w-[310px] flex-shrink-0 rounded-[10px]`}
            >
              <div className="flex justify-between items-center w-full pb-3">
                <span className='mt-px checkbox-alignment leading-none'>
                  <input
                    type='checkbox'
                    name={`item-${index}`}
                    checked={item?.selected || false}
                    onChange={(e) => handleChange(index, e.target.checked)}
                    style={{ width: '18px', height: '18px', border: '1px solid #ccc', cursor: 'pointer' }}
                  />
                </span>
                <img
                  onClick={() => setFavorite(index, !item?.is_favorite)}
                  className='w-6 h-6 cursor-pointer ml-auto'
                  src={
                    item?.is_favorite ? allImgPaths.heartIconRed : allImgPaths.heartIconGrey
                  }
                  alt=''
                />
              </div>

              <div className="">
                <h4 title={item?.title} className='text-[#101828] font-interSemiBold text-lg px-0.5 pb-1 line-clamp-2 max-w-96 h-[60px]'>{item?.title}</h4>
                <p className='text-[#475467] px-0.5 mb-3 line-clamp-6 h-24'>{item?.description}</p>
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex items-center gap-x-2 justify-end pt-5">
        {
          !isEditMode &&
          <button
            type="button"
            className={`text-sm text-white bg-[#1E0AF5] hover:bg-[#120693] font-interMedium rounded-lg px-8 py-2`}
            onClick={() => showMore()}
          >
            Show More
          </button>
        }
        <button
          type="button"
          className={`text-sm text-white bg-[#1E0AF5] hover:bg-[#120693] font-interMedium rounded-lg px-8 py-2 ${saveBtnDisable ? 'button-disabled' : ''}`}
          onClick={() => save()}
          disabled={saveBtnDisable}
        >
          Shortlist
        </button>
      </div>
    </div>
  );
}

export default ListItems;
